import { useMemo } from 'react';
import { NodeViewProps } from '@tiptap/react';
import { InlineWrapper } from './containers';
import { InlineAssetContextProvider, useInlineAsset } from './context';
import {
  DisplayControls,
  InlineAssetDisplay,
  InlineAssetEdit,
  InlineAssetProperties,
} from './components';
import { useAssetConfigContext } from 'screens/editorial/asset/context/AssetConfigContextProvider';

const InlineAsset = () => {
  const { nodeID, inlineProps, inlineRecord, view, isLoading, isShowButtons } =
    useInlineAsset();
  const { isReadOnlyMode } = useAssetConfigContext();

  const showDisplayControls = view === 'display' && !isReadOnlyMode && isShowButtons;

  if (isLoading || inlineProps.getPos() === undefined || view === null) return null;

  return (
    <InlineWrapper className={`blox-inline__wrapper-${inlineRecord?.type} ${nodeID}`}>
      {showDisplayControls && <DisplayControls />}
      {view === 'display' && <InlineAssetDisplay />}
      {view === 'edit' && <InlineAssetEdit />}
      {view === 'properties' && <InlineAssetProperties />}
    </InlineWrapper>
  );
};

export const InlineAssetView = (inlineProps: NodeViewProps) => {
  // Extract node values to avoid complex dependency expressions
  const uuid = inlineProps.node.attrs.uuid;
  const type = inlineProps.node.attrs.type;
  const importId = inlineProps.node.attrs.importId;
  const pos = inlineProps.getPos?.() ?? 0;

  // Create a stable key based on node identity to force remount on node changes.
  // Priority: importId (stable through entire upload→resolve lifecycle, prevents
  // destructive remount when uuid is assigned after binaryConvert success)
  // > uuid (resolved identity for non-upload nodes) > pos (fallback for new
  // placeholder nodes). The importId attr is cleared from the node after the
  // import completes, at which point the key transitions to uuid-based.
  const nodeKey = useMemo(() => {
    if (importId) return `import-${importId}`;
    if (uuid) return `uuid-${uuid}`;
    return `pos-${type}-${pos}`;
  }, [uuid, importId, type, pos]);
  return (
    <InlineAssetContextProvider
      key={nodeKey}
      inlineProps={inlineProps}
    >
      <InlineAsset />
    </InlineAssetContextProvider>
  );
};

export default InlineAssetView;
